import {
  type ColorValue,
  CssColor,
  CssFont,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParams,
  ISettingsColor,
  ISettingsFont,
  ISiteTextPreset,
  IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';

export type IStylesParams = {
  memberAreaDesignTitleColor: StyleParamType.Color;
  memberAreaDesignTitleFont: StyleParamType.Font;
  memberAreaDesignDescriptionColor: StyleParamType.Color;
  memberAreaDesignDescriptionFont: StyleParamType.Font;
  memberAreaDesignTextColor: StyleParamType.Color;
  memberAreaDesignTextFont: StyleParamType.Font;
  memberAreaDesignSecondaryTextColor: StyleParamType.Color;
  memberAreaDesignSecondaryTextFont: StyleParamType.Font;
  memberAreaDesignLinksColor: StyleParamType.Color;
  memberAreaDesignLinksFont: StyleParamType.Font;
  memberAreaDesignNoBookingsButtonTextColor: StyleParamType.Color;
  memberAreaDesignNoBookingsButtonFont: StyleParamType.Font;
  memberAreaDesignNoBookingsButtonColor: StyleParamType.Color;
  memberAreaDesignNoBookingsButtonBorderColor: StyleParamType.Color;
  memberAreaDesignNoBookingsButtonBorderWidth: StyleParamType.Number;
  memberAreaDesignNoBookingsButtonBorderRadius: StyleParamType.Number;
  memberAreaDesignSelectedTabBorderColor: StyleParamType.Color;
  memberAreaDesignTabsBorderColor: StyleParamType.Color;
  memberAreaDesignListDividerColor: StyleParamType.Color;
  memberAreaDesignListDividerWidth: StyleParamType.Number;
  memberAreaDesignManageButtonTextColor: StyleParamType.Color;
  memberAreaDesignManageButtonColor: StyleParamType.Color;
  memberAreaDesignManageBorderColor: StyleParamType.Color;
  memberAreaDesignManageFont: StyleParamType.Font;
  memberAreaDesignManageButtonBorderWidth: StyleParamType.Number;
  memberAreaDesignManageButtonBorderRadius: StyleParamType.Number;
  memberAreaDesignBGColorBordersColor: StyleParamType.Color;
  memberAreaDesignBorderColor: StyleParamType.Color;
  memberAreaDesignBorderWidth: StyleParamType.Number;
  memberAreaDesignBorderRadius: StyleParamType.Number;
};

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

type CssColorValue = Parameters<typeof wixColor>[0];

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

type CssFontValue = Parameters<typeof wixFont>[0];

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
  },
});

export default createStylesParams<IStylesParams>({
  memberAreaDesignTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  memberAreaDesignTitleFont: fontParam(
    'Page-title',
    { size: 28 },
    CssFont.WstHeading2Font,
  ),
  memberAreaDesignDescriptionColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  memberAreaDesignDescriptionFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  memberAreaDesignTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  memberAreaDesignTextFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  memberAreaDesignSecondaryTextColor: colorParam(
    StyleType.color,
    'color-4',
    CssColor.WstShade3Color,
  ),
  memberAreaDesignSecondaryTextFont: fontParam(
    'Body-M',
    { size: 14 },
    CssFont.WstParagraph2Font,
  ),
  memberAreaDesignLinksColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  memberAreaDesignLinksFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  memberAreaDesignNoBookingsButtonFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  memberAreaDesignNoBookingsButtonColor: colorParamWithOpacity(
    StyleType.color,
    'color-8',
    1,
    CssColor.WstLinksAndActionsColor,
  ),
  memberAreaDesignNoBookingsButtonTextColor: colorParamWithOpacity(
    StyleType.color,
    'color-1',
    1,
    CssColor.WstButtonPrimaryColor,
  ),
  memberAreaDesignNoBookingsButtonBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-8',
    1,
    CssColor.WstButtonPrimaryBorderRightColor,
  ),
  memberAreaDesignNoBookingsButtonBorderWidth: numberParam(
    StyleType.borderWidth,
    1,
  ),
  memberAreaDesignNoBookingsButtonBorderRadius: numberParam(
    StyleType.borderRadius,
    0,
  ),
  memberAreaDesignSelectedTabBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-8',
    1,
    CssColor.WstLinksAndActionsColor,
  ),
  memberAreaDesignTabsBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  memberAreaDesignManageButtonTextColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  memberAreaDesignManageButtonColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  memberAreaDesignManageBorderColor: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  memberAreaDesignManageFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  memberAreaDesignManageButtonBorderWidth: numberParam(
    StyleType.borderWidth,
    1,
  ),
  memberAreaDesignManageButtonBorderRadius: numberParam(
    StyleType.borderRadius,
    0,
  ),
  memberAreaDesignListDividerColor: colorParamWithOpacity(
    StyleType.color,
    'color-5',
    0.3,
    CssColor.WstSystemLine1Color,
  ),
  memberAreaDesignListDividerWidth: numberParam(StyleType.width, 1),
  memberAreaDesignBGColorBordersColor: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-1',
    1,
    CssColor.WstPrimaryBackgroundColor,
  ),
  memberAreaDesignBorderColor: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.3,
    CssColor.WstSystemLine1Color,
  ),
  memberAreaDesignBorderWidth: numberParam(StyleType.borderWidth, 0),
  memberAreaDesignBorderRadius: numberParam(StyleType.borderRadius, 0),
});
